import axios from 'axios';
import config from '../config/config';
import { refreshAccessToken } from './tokenRefresh';

// Функция для определения API base URL
const getApiBaseURL = (): string => {
  // Если указана переменная окружения, используем её
  if (process.env.REACT_APP_API_URL) {
    // Заменяем /api/v1 на /api/v2 если нужно
    return process.env.REACT_APP_API_URL.replace('/api/v1', '/api/v2');
  }
  
  // Если мы в браузере и загружены не с localhost, используем текущий хост
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    
    // Если загружены с домена ryaskov.pro, используем api.ryaskov.pro
    if (currentHost === 'ryaskov.pro' || currentHost === 'www.ryaskov.pro') {
      return `https://api.ryaskov.pro/api/v2`;
    }
    
    // Если загружены с IP адреса (не localhost и не 127.0.0.1), используем этот IP для API
    // Проверяем, что это IP адрес (содержит точку, но не доменное имя)
    const isIPAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(currentHost);
    if (currentHost !== 'localhost' && currentHost !== '127.0.0.1' && isIPAddress) {
      // Если это IP сервера с nginx, используем тот же IP без порта (через nginx)
      const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
      return `${protocol}://${currentHost}/api/v2`;
    }
  }
  
  // Используем config если доступен
  if (config?.api?.baseURL) {
    return config.api.baseURL.replace('/api/v1', '/api/v2');
  }
  
  // По умолчанию используем localhost
  return 'http://localhost:5000/api/v2';
};

// Создаем отдельный axios instance для v2 API без префикса /v1
const axiosV2Instance = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления токена к каждому запросу v2
axiosV2Instance.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    
    // Если данные - FormData, удаляем Content-Type, чтобы браузер сам установил правильный заголовок с boundary
    // Это особенно важно для мобильных браузеров
    if (requestConfig.data instanceof FormData) {
      // Удаляем Content-Type из headers, чтобы браузер сам установил multipart/form-data с boundary
      delete requestConfig.headers['Content-Type'];
      delete requestConfig.headers['content-type'];
      // Также удаляем из defaults, если там установлен
      if (requestConfig.headers.common) {
        delete requestConfig.headers.common['Content-Type'];
        delete requestConfig.headers.common['content-type'];
      }
    }
    
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosV2Instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config;
    const url: string = originalRequest?.url || '';

    if (status === 401 && !originalRequest._retry &&
        !url.includes('/auth/refresh') && !url.includes('/auth/login')) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        axiosV2Instance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return axiosV2Instance(originalRequest);
      } catch (refreshError) {
        delete axiosV2Instance.defaults.headers.common['Authorization'];
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosV2Instance;

